/**
 * App Layer telemetry — the FR-20 success events.
 *
 * These are **foundation events, not app-local analytics**: they land in the
 * canonical `audit_events` table that the onboarding-funnel dashboard already
 * reads, so App Layer activation shows up on a surface that exists rather than
 * needing a new one. No new table (Doc 31 first law).
 *
 * ## What may go in a payload
 *
 * Operational signal ONLY — app ids, tool ids, counts, durations, coarse
 * states. **Never item titles, descriptions, criteria text, or any other
 * content.** These events are the same shape a hub would be allowed to see
 * under Commons tier T1 (PRD §9), and that tier is defined as "counts,
 * durations, tool/App ids — no titles, no content". Keeping content out here is
 * what makes that tier boundary real rather than aspirational.
 *
 * ## Failure policy
 *
 * Telemetry must never break a user action. Every writer swallows its errors
 * and returns void — a failed metric is strictly less bad than a failed
 * decision.
 */

import { getSafeSupabaseClient, getCurrentWorkspaceId } from './supabaseSync';
import { useUserStore } from '../stores/prioritization/userStore';

/** The FR-20 event vocabulary. */
export type AppTelemetryEventType =
  /** First time this user has opened this App at all. */
  | 'app.first_open'
  /** The App's guided setup produced its first real artifact. */
  | 'app.setup_completed'
  /** The App's terminal artifact was reached (e.g. a session's result locked). */
  | 'decision.completed'
  /** That artifact was published to the record. */
  | 'decision.published'
  /** The escape hatch was taken — the user went from an App into the full platform. */
  | 'app.promoted'
  /** The Navigator was opened (PRD-navigator-that-does-it FR-0). `state` says from where. */
  | 'navigator.opened'
  /** A sentence was asked. `toolId` is the top entry, `state` how it was found, `count` the matches. */
  | 'navigator.asked'
  /** The person went somewhere from the Navigator. `toolId` is the entry or next step. */
  | 'navigator.went';

/** Operational-only payload. Content-free by contract (see module docstring). */
export interface AppTelemetryPayload {
  /** Registry appId — 'session', 'items', 'focus', … */
  appId: string;
  /** Tool/method id where relevant (e.g. 'dot_voting'). Never free text. */
  toolId?: string;
  /** Counts, never contents. */
  count?: number;
  /** Milliseconds, for time-to-first-decision style metrics. */
  durationMs?: number;
  /** Coarse state label from a canonical enum (e.g. session state). */
  state?: string;
  /** Where a promotion landed — a route family, not a record id. */
  target?: string;
}

/**
 * A path as a route family, never a record: every segment that is an id (a
 * UUID, or anything with a digit) becomes `:id`, so `target` carries no record.
 */
export function routeFamily(pathname: string): string {
  return pathname
    .split('?')[0]
    .split('/')
    .map((seg) => (/\d/.test(seg) ? ':id' : seg))
    .join('/');
}

/**
 * Write one App Layer event.
 *
 * `workspace_id` is set explicitly: the onboarding-funnel dashboard filters on
 * it, and the pre-existing helpers (`recordWorkflowEvent`, `recordAuthEvent`)
 * omit it — so events written through those are invisible to that dashboard.
 * This one is not.
 */
export async function recordAppEvent(
  eventType: AppTelemetryEventType,
  payload: AppTelemetryPayload,
): Promise<void> {
  try {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const workspaceId = getCurrentWorkspaceId();
    const userId = useUserStore.getState().userId;

    await supabase.from('audit_events').insert({
      event_type: eventType,
      payload: payload as unknown as Record<string, unknown>,
      created_by: userId || 'unknown',
      ...(workspaceId ? { workspace_id: workspaceId } : {}),
    });
  } catch {
    // Telemetry never breaks the action it observes.
  }
}

/**
 * Emit `app.first_open` at most once per (user, app).
 *
 * Deliberately a preflight read rather than a unique index: this is telemetry,
 * not a governance record, and an index on a hot audit table is a real cost to
 * pay for a metric. The read/write race can in principle double-count a user
 * who opens the same App twice in the same instant on two devices — acceptable
 * for an activation funnel, and cheaper to tolerate than to prevent.
 */
export async function recordAppFirstOpen(appId: string): Promise<void> {
  try {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const userId = useUserStore.getState().userId;
    if (!userId || userId === 'unknown') return;

    const { data, error } = await supabase
      .from('audit_events')
      .select('id')
      .eq('event_type', 'app.first_open')
      .eq('created_by', userId)
      .contains('payload', { appId })
      .limit(1);

    if (error) return;
    if (data && data.length > 0) return; // already recorded

    await recordAppEvent('app.first_open', { appId });
  } catch {
    // Never break the render path.
  }
}

/** Convenience wrappers so call sites read as intent, not as plumbing. */
export const appTelemetry = {
  firstOpen: recordAppFirstOpen,
  setupCompleted: (appId: string, extra: Omit<AppTelemetryPayload, 'appId'> = {}) =>
    recordAppEvent('app.setup_completed', { appId, ...extra }),
  decisionCompleted: (appId: string, extra: Omit<AppTelemetryPayload, 'appId'> = {}) =>
    recordAppEvent('decision.completed', { appId, ...extra }),
  decisionPublished: (appId: string, extra: Omit<AppTelemetryPayload, 'appId'> = {}) =>
    recordAppEvent('decision.published', { appId, ...extra }),
  promoted: (appId: string, target: string) =>
    recordAppEvent('app.promoted', { appId, target }),
};
